import { getMetricLabel } from '@vizit/constants';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { BaseMetricCard } from '../base-metric-card';
import { BarChart } from '../metric-charts/bar-chart';
import type { BarMetric } from '../metric-charts/types';

interface VisualDiversityMetricCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The bar metric data to display
   */
  metric: BarMetric;
  /**
   * Optional description text to display below the title
   */
  description?: string;
  /**
   * Width of the chart
   * @default 300
   */
  chartWidth?: number;
  /**
   * Height of the chart
   * @default 60
   */
  chartHeight?: number;
  /**
   * Number of filled zaps for impact badge (0-4)
   */
  filledZapCount: number;
  /**
   * Whether the card is in loading state
   * @default false
   */
  isLoading?: boolean;
}

/**
 * VisualDiversityMetricCard displays a visual diversity metric with bar chart and impact badge
 */
const VisualDiversityMetricCard = React.forwardRef<HTMLDivElement, VisualDiversityMetricCardProps>(
  (props, forwardedRef) => {
    const {
      metric,
      description,
      chartWidth = 300,
      chartHeight = 60, // score-threshold-allow: chart height in px
      filledZapCount,
      isLoading = false,
      ...other
    } = props;

    const [isHovered, setIsHovered] = React.useState(false);
    const label = getMetricLabel(metric.key);

    return (
      <BaseMetricCard
        ref={forwardedRef}
        label={label}
        description={description}
        filledZapCount={filledZapCount}
        isLoading={isLoading}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        footer={
          <div
            className={cx(
              'flex justify-between items-center text-xs text-gray-utility-500 h-[1.2em]',
              isHovered ? 'opacity-100' : 'opacity-0',
            )}
          >
            <span>Similar</span>
            <span>Diverse</span>
          </div>
        }
        {...other}
      >
        <BarChart metric={metric} width={chartWidth} height={chartHeight} />
      </BaseMetricCard>
    );
  },
);

VisualDiversityMetricCard.displayName = 'VisualDiversityMetricCard';

export type { VisualDiversityMetricCardProps };
export { VisualDiversityMetricCard };
